import { isPlatformBrowser } from "@angular/common";
import { Component, Inject, OnDestroy, PLATFORM_ID, ChangeDetectorRef } from "@angular/core";
import {
  faMousePointer,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { TranslationHelperService } from "../../services/translations/translation-helper.service";
import { unsubscribe } from "../../utils/helpers";
import { Subscription } from "rxjs";
import { switchMap, take, tap } from "rxjs/operators";
import { Indicator } from "./models/indicator";
import { ActivatedRoute } from "@angular/router";
import { arraySet } from "../../utils/arrays";
import { ProjectService } from "src/app/services/project.service";
import { IndicatorsService } from "./indicators.service";

type RequestState = "loading" | "ready" | "error";

@Component({
  standalone: false,
  selector: "app-indicators-page-component",
  templateUrl: "./indicators-page.component.html",
  styleUrls: ["./indicators-page.component.scss"],
})
export class IndicatorsPageComponent implements OnDestroy {
  private readonly subscriptions: Subscription[] = [];
  private readonly isBrowser: boolean;

  readonly icons = {
    loader: faSpinner,
    noSelection: faMousePointer,
  };

  indicators: Indicator[] | null = null;
  requestState: RequestState = "loading";
  error: unknown;

  group: string;
  short: string;
  breakdown: string;
  unit: string;

  get groups(): string[] {
    return arraySet((this.indicators || []).map((i) => i.group));
  }

  get shorts(): string[] {
    return arraySet(
      (this.indicators || []).filter((i) => i.group === this.group).map((i) => i.short)
    );
  }

  get breakdowns(): string[] {
    return arraySet(
      (this.indicators || [])
        .filter((i) => i.group === this.group && i.short === this.short)
        .map((i) => i.breakdown)
    );
  }

  get units(): string[] {
    return arraySet(
      (this.indicators || [])
        .filter(
          (i) =>
            i.group === this.group &&
            i.short === this.short &&
            i.breakdown === this.breakdown
        )
        .map((i) => i.unit)
    );
  }

  get indicator(): Indicator {
    return (this.indicators || []).find(
      (i) =>
        i.group === this.group &&
        i.short === this.short &&
        i.breakdown === this.breakdown &&
        i.unit === this.unit
    );
  }

  constructor(
    private readonly indicatorsService: IndicatorsService,
    private readonly translationHelper: TranslationHelperService,
    private readonly route: ActivatedRoute,
    private readonly projectService: ProjectService,
    private readonly cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    const group = projectService.groups.filter((c) => c.group == "Data")[0];
    projectService.selectGroup(group);
    unsubscribe(this.subscriptions);

    if (!this.isBrowser) {
      this.requestState = "ready";
      return;
    }

    this.subscriptions.push(
      this.translationHelper
        .getLanguage()
        .pipe(
          take(1),
          tap(() => {
            this.requestState = "loading";
            this.cdr.markForCheck();
            this.error = undefined;
            this.indicators = null;
            this.group = undefined;
            this.short = undefined;
            this.breakdown = undefined;
            this.unit = undefined;
          }),
          switchMap((language) => this.indicatorsService.getIndicators(language))
        )
        .subscribe({
          next: (indicators) => {
            this.indicators = indicators;
            this.updateGroups();
            this.requestState = "ready";
            this.cdr.markForCheck();
            console.log("[Indicators] menu ready", { groups: this.groups.length, shorts: this.shorts.length, breakdowns: this.breakdowns.length, units: this.units.length, selected: { group: this.group, short: this.short, breakdown: this.breakdown, unit: this.unit } });
          },
          error: (err) => {
            this.indicators = [];
            this.group = undefined;
            this.short = undefined;
            this.breakdown = undefined;
            this.unit = undefined;
            this.error = err;
            this.requestState = "error";
            this.cdr.markForCheck();
            console.error("[Indicators] indicator list request failed", err);
          },
        })
    );
  }

  ngOnDestroy() {
    unsubscribe(this.subscriptions);
  }

  updateGroups() {
    const groups = this.groups;

    if (groups.length) {
      const group = this.route.snapshot.queryParamMap.get("group");
      this.group = group && groups.includes(group) ? group : groups[0];
    } else {
      this.group = undefined;
    }
    this.updateShorts();
  }

  updateShorts() {
    const shorts = this.shorts;

    if (shorts.length) {
      const short = this.route.snapshot.queryParamMap.get("short");
      this.short = short && shorts.includes(short) ? short : shorts[0];
    } else {
      this.short = undefined;
    }
    this.updateBreakdown();
  }

  updateBreakdown() {
    const breakdowns = this.breakdowns;

    if (breakdowns.length) {
      const breakdown = this.route.snapshot.queryParamMap.get("breakdown");
      this.breakdown = breakdown && breakdowns.includes(breakdown) ? breakdown : breakdowns[0];
    } else {
      this.breakdown = undefined;
    }

    this.updateUnit();
  }

  updateUnit() {
    const units = this.units;

    if (units.length) {
      const unit = this.route.snapshot.queryParamMap.get("unit");
      this.unit = unit && units.includes(unit) ? unit : units[0];
    } else {
      this.unit = undefined;
    }
  }
}
